import { useCreateAdminUserMutation } from '@codezo/graphql';
import { Button, Group, Modal, Stack, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconMail, IconUser } from '@tabler/icons-react';
import { useState } from 'react';

interface InviteUserModalProps {
	opened: boolean;
	onClose: () => void;
	onSuccess: () => void;
}

interface InviteFormData {
	email: string;
	firstName: string;
	lastName: string;
}

export function InviteUserModal({ opened, onClose, onSuccess }: InviteUserModalProps) {
	const [loading, setLoading] = useState(false);

	const form = useForm<InviteFormData>({
		initialValues: {
			email: '',
			firstName: '',
			lastName: '',
		},
		validate: {
			email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
			firstName: (value) =>
				value.length < 2 ? 'First name must be at least 2 characters' : null,
			lastName: (value) =>
				value.length < 2 ? 'Last name must be at least 2 characters' : null,
		},
	});

	const handleClose = () => {
		form.reset();
		setLoading(false);
		onClose();
	};

	const [createUser] = useCreateAdminUserMutation({
		onCompleted: (data) => {
			if (data.createAdminUser.success) {
				notifications.show({
					title: 'Admin User Created',
					message: 'Admin user has been successfully created',
					color: 'green',
					icon: <IconCheck size={16} />,
				});
				handleClose();
				onSuccess();
			} else {
				notifications.show({
					title: 'Creation Failed',
					message: data.createAdminUser.message || 'Failed to create admin user',
					color: 'red',
				});
			}
		},
		onError: (error) => {
			notifications.show({
				title: 'Creation Failed',
				message: error.message || 'An error occurred while creating the admin user',
				color: 'red',
			});
		},
	});

	const handleSubmit = async () => {
		const validation = form.validate();
		if (validation.hasErrors) return;

		setLoading(true);
		try {
			await createUser({
				variables: {
					input: {
						email: form.values.email,
						firstName: form.values.firstName,
						lastName: form.values.lastName,
					},
				},
			});
		} catch {
			// Error is already handled by mutation onError
		} finally {
			setLoading(false);
		}
	};

	return (
		<Modal
			opened={opened}
			onClose={handleClose}
			title={
				<Group gap="sm">
					<IconUser size={20} />
					<Title order={4}>Create Admin User</Title>
				</Group>
			}
			size="md"
			centered
		>
			<Stack gap="md">
				<Group grow>
					<TextInput
						label="First Name"
						placeholder="John"
						leftSection={<IconUser size={16} />}
						{...form.getInputProps('firstName')}
						required
					/>
					<TextInput
						label="Last Name"
						placeholder="Doe"
						leftSection={<IconUser size={16} />}
						{...form.getInputProps('lastName')}
						required
					/>
				</Group>
				<TextInput
					label="Email"
					placeholder="user@example.com"
					leftSection={<IconMail size={16} />}
					{...form.getInputProps('email')}
					required
				/>

				<Group justify="flex-end" gap="sm" mt="md">
					<Button variant="default" onClick={handleClose} disabled={loading}>
						Cancel
					</Button>
					<Button
						onClick={handleSubmit}
						loading={loading}
						leftSection={<IconCheck size={16} />}
					>
						Create User
					</Button>
				</Group>
			</Stack>
		</Modal>
	);
}
